import panelCss from "./MusicPlayerPanel.sass?inline"
import {createElement} from "@opendaw/lib-jsx"
import {Html} from "@opendaw/lib-dom"
import {StoredTrack, formatDuration} from "./TrackStore"

const className = Html.adoptStyleSheet(panelCss, "MusicPlayerPanel")

let container: HTMLDivElement | null = null
let audioEl: HTMLAudioElement | null = null
let rafHandle = 0

function formatTime(sec: number): string {
    const m = Math.floor(sec / 60)
    const s = Math.floor(sec % 60)
    return `${m}:${s.toString().padStart(2, "0")}`
}

function getTimeAgo(ts: number): string {
    const diff = Date.now() - ts
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return "Just now"
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 7) return `${days}d ago`
    return new Date(ts).toLocaleDateString()
}

export function closePlayer(): void {
    if (!container) return
    container.classList.remove("open")
    cancelAnimationFrame(rafHandle)
    if (audioEl) {
        audioEl.pause()
        audioEl = null
    }
    const c = container
    container = null
    setTimeout(() => c.remove(), 420)
}

function proxiedAudioUrl(url: string): string {
    return `/api/proxy-audio?url=${encodeURIComponent(url)}`
}

export function openPlayer(track: StoredTrack): void {
    if (!track.audioUrl) return
    closePlayer()
    const audio = new Audio(proxiedAudioUrl(track.audioUrl))
    audioEl = audio
    const seekBar: HTMLInputElement = (
        <input type="range" className="seek-bar" min="0" max="1000" value="0" step="1"/>
    ) as unknown as HTMLInputElement
    const timeCurrent: HTMLSpanElement = <span className="time-current">0:00</span> as unknown as HTMLSpanElement
    const timeDuration: HTMLSpanElement = <span className="time-duration">—:——</span> as unknown as HTMLSpanElement
    const playBtn: HTMLButtonElement = <button className="play-btn">▶</button> as unknown as HTMLButtonElement
    const vizBars: HTMLDivElement = <div className="viz-bars"/> as unknown as HTMLDivElement
    for (let i = 0; i < 12; i++) {
        const bar: HTMLDivElement = <div className="bar"/> as unknown as HTMLDivElement
        bar.style.animationDelay = `${i * 0.07}s`
        bar.style.animationDuration = `${0.7 + (i % 4) * 0.17}s`
        vizBars.appendChild(bar)
    }
    const volSlider: HTMLInputElement = (
        <input type="range" className="vol-slider" min="0" max="100" value="80" step="1"/>
    ) as unknown as HTMLInputElement
    audio.volume = 0.8
    function tick(): void {
        const pct = (audio.currentTime / (audio.duration || 1)) * 100
        seekBar.value = String(Math.round(pct * 10))
        seekBar.style.setProperty("--seek-pct", `${pct.toFixed(1)}%`)
        timeCurrent.textContent = formatTime(audio.currentTime)
        if (!audio.paused && !audio.ended) rafHandle = requestAnimationFrame(tick)
    }
    playBtn.onclick = () => {
        if (audio.paused) {
            audio.play().then(() => {
                playBtn.textContent = "⏸"
                vizBars.classList.add("playing")
                rafHandle = requestAnimationFrame(tick)
            }).catch(err => {
                console.warn("Audio play failed:", err)
            })
        } else {
            audio.pause()
            playBtn.textContent = "▶"
            vizBars.classList.remove("playing")
        }
    }
    seekBar.oninput = () => {
        const pct = Number(seekBar.value) / 1000
        audio.currentTime = pct * (audio.duration || 0)
        seekBar.style.setProperty("--seek-pct", `${(pct * 100).toFixed(1)}%`)
    }
    volSlider.oninput = () => { audio.volume = Number(volSlider.value) / 100 }
    audio.onended = () => {
        playBtn.textContent = "▶"
        vizBars.classList.remove("playing")
    }
    audio.onloadedmetadata = () => { timeDuration.textContent = formatTime(audio.duration) }
    const closeBtn: HTMLButtonElement = <button className="close-btn">✕</button> as unknown as HTMLButtonElement
    closeBtn.onclick = closePlayer
    const restartBtn: HTMLButtonElement = <button className="ctrl-btn">⏮</button> as unknown as HTMLButtonElement
    restartBtn.onclick = () => {
        audio.currentTime = 0
        seekBar.value = "0"
        seekBar.style.setProperty("--seek-pct", "0%")
        timeCurrent.textContent = "0:00"
    }
    const backdrop: HTMLDivElement = <div className="backdrop"/> as unknown as HTMLDivElement
    backdrop.onclick = closePlayer
    const panel: HTMLDivElement = (
        <div className="panel">
            {closeBtn}
            <div className="art-section">
                <div className="art-box">
                    {vizBars}
                </div>
            </div>
            <div className="track-info">
                <div className="track-title">{track.title}</div>
                <div className="track-desc">{track.description}</div>
                <div className="track-tags">
                    <span className="tag">{track.model}</span>
                    <span className="tag">{track.bpm} BPM</span>
                    <span className="tag">{formatDuration(track.duration)}</span>
                    <span className="tag">{getTimeAgo(track.createdAt)}</span>
                </div>
            </div>
            <div className="seek-section">
                {seekBar}
                <div className="time-row">
                    {timeCurrent}
                    {timeDuration}
                </div>
            </div>
            <div className="controls">
                {restartBtn}
                {playBtn}
                <button className="ctrl-btn">⏭</button>
            </div>
            <div className="vol-row">
                <span className="vol-icon">🔈</span>
                {volSlider}
                <span className="vol-icon">🔊</span>
            </div>
        </div>
    ) as unknown as HTMLDivElement
    container = (
        <div className={className}>
            {backdrop}
            {panel}
        </div>
    ) as unknown as HTMLDivElement
    document.body.appendChild(container)
    requestAnimationFrame(() => container?.classList.add("open"))
}
